import { SerializedNode, SerializedNodes, useNode } from '@craftjs/core';
import { isNullOrEmpty } from 'src/helpers/string-helper';
import { useContextSelector } from 'use-context-selector';
import i18next from 'i18next';
import {
  ConditionalProps,
  FormFieldProps,
  FormTitleProps,
  isAttachmentsField,
  isFieldWithOptions,
  isSliderField,
} from '../types/BaseControlTypes';
import { LayoutValidationContext, ValidationEnum, ValidationResultType } from './layoutValidation.context';
import { getDataKeysFromLayout } from '../layout/panels/helpers/helper';
import { NodeOperatorType, operatorsWithNotNullValue } from '../layout/panels/helpers/expression/types';
import ExpressionBuilder from '../layout/panels/helpers/expression/expressionBuilder';
import _ from 'lodash';
import { executeSimpleExpression } from '../../monaco/executor';
import { IBaseScoreSection, IBaseVariableSection } from 'src/services/api/api-client';

const dataKeyRegex = /^[a-zA-Z0-9_]+$/g;

export const isFormFieldProps = (props: any): props is FormFieldProps =>
  (props as FormFieldProps).dataKey !== undefined;

export const isConditionalProps = (props: any): props is ConditionalProps =>
  (props as ConditionalProps).enableIf !== undefined || (props as ConditionalProps).visibleIf !== undefined;

export const validateDataKey = (layout: SerializedNodes, dataKey?: string) => {
  if (isNullOrEmpty(dataKey)) return i18next.t('GeneralFormBuilder.Errors.DataKeyIsEmpty');

  const isValid = new RegExp(dataKeyRegex).test(dataKey!);
  if (!isValid) return i18next.t('GeneralFormBuilder.Errors.DataKeyNotValid');

  const dataKeys = getDataKeysFromLayout(layout).map((x) => x.dataKey);
  if (dataKeys.some((x) => x?.toLowerCase() === dataKey?.toLowerCase()))
    return i18next.t('GeneralFormBuilder.Errors.DataKeyExists');

  return undefined;
};

export const generalSchemeValidation = (
  layout: SerializedNodes | undefined,
  scoreSections?: IBaseScoreSection[],
  variables?: IBaseVariableSection[],
) => {
  const dataKeyResult = checkDataKeyValidation(layout);
  const checkOptionsResult = checkOptionsScoreWeight(layout, scoreSections, variables);
  const checkNoAnswerScoreValueResult = checkNoAnswerScore(layout, scoreSections, variables);
  const markStepResult = checkSliderStepMarks(layout);
  const fileTypesResult = checkFileTypes(layout);
  const expressionValidationResult = validateExpressions(layout);

  return {
    ...dataKeyResult,
    ...checkOptionsResult,
    ...checkNoAnswerScoreValueResult,
    ...markStepResult,
    ...fileTypesResult,
    ...expressionValidationResult,
  };
};

export const checkDataKeyValidation = (layout: SerializedNodes | undefined): ValidationResultType => {
  if (!layout) return {};

  return Object.entries(layout).reduce((obj, [nodeId, node]) => {
    const { props } = node;
    if (isFormFieldProps(props)) {
      const { dataKey } = props;
      const errorMessage = validateDataKey(layout, dataKey);
      if (isNullOrEmpty(errorMessage))
        return {
          ...obj,
          [nodeId]: {
            type: ValidationEnum.DataKey,
            message: errorMessage,
          },
        };

      return obj;
    }

    return obj;
  }, {} satisfies ValidationResultType);
};

export const useDataKeyValidation = () => {
  const { id } = useNode();

  return useContextSelector(LayoutValidationContext, (x) => x.layoutValidationResult[id]);
};

// If a field exists in an expression, all options must have a score weight.
export const checkOptionsScoreWeight = (
  layout: SerializedNodes | undefined,
  scoreSections?: IBaseScoreSection[],
  variables?: IBaseVariableSection[],
): ValidationResultType => {
  return Object.entries(layout ?? {}).reduce((result, [nodeId, node]) => {
    if (!isFieldWithOptions(node.props)) return { ...result };

    if (
      (isScoreSectionsContainsDataKey(node.props.dataKey, scoreSections) ||
        isVariableContainsDataKey(node.props.dataKey, variables)) &&
      node.props.options.some((x) => x.scoreWeight === undefined)
    )
      return {
        ...result,
        [nodeId]: {
          type: ValidationEnum.OptionWeight,
          message: i18next.t('GeneralFormBuilder.Errors.OptionWeight'),
        },
      };

    return { ...result };
  }, {});
};

// If a field exists in an expression, all options must have a score weight.
export const checkNoAnswerScore = (
  layout: SerializedNodes | undefined,
  scoreSections?: IBaseScoreSection[],
  variables?: IBaseVariableSection[],
): ValidationResultType => {
  return Object.entries(layout ?? {}).reduce((result, [nodeId, node]) => {
    // @ts-ignore
    // Not a number fields don't have 'No answer score'
    if (nanValueResolvedNames.includes(node.type.resolvedName)) return { ...result };

    if (
      (isScoreSectionsContainsDataKey(node.props.dataKey, scoreSections) ||
        isVariableContainsDataKey(node.props.dataKey, variables)) &&
      node.props.isOptional &&
      node.props.noAnswerScore === undefined
    )
      return {
        ...result,
        [nodeId]: {
          type: ValidationEnum.NoAnswerScore,
          message: i18next.t('GeneralFormBuilder.Errors.NoAnswerScore'),
        },
      };

    return { ...result };
  }, {});
};

export const validateExpressions = (layout: SerializedNodes | undefined): ValidationResultType => {
  if (!layout) return {};

  const dataKeys = getDataKeysFromLayout(layout).map((x) => x.dataKey);
  return Object.entries(layout ?? {}).reduce((result, [nodeId, node]) => {
    if (
      !isConditionalProps(node.props) ||
      (expressionIsValid(node.props.enableIf, dataKeys, layout) &&
        expressionIsValid(node.props.visibleIf, dataKeys, layout))
    )
      return { ...result };

    return {
      ...result,
      [nodeId]: {
        type: ValidationEnum.ConditionalExpression,
        message: i18next.t('GeneralFormBuilder.Errors.ConditionalExpression'),
      },
    };
  }, {});
};

const expressionIsValid = (str: string | undefined, dataKeys: string[], layout: SerializedNodes) => {
  if (!str) return true;

  try {
    const expression = ExpressionBuilder.fromString(str);
    const dataKeysIsValid = expression
      .toArray()
      .nodes.every(
        (x) => dataKeys.includes(x.dataKey) && fieldWithOptionsIsValid(x.dataKey, x.value, layout, x.operator),
      );

    return dataKeysIsValid;
  } catch (error: any) {
    return false;
  }
};

const fieldWithOptionsIsValid = (
  dataKey: string,
  value: string | string[],
  layout: SerializedNodes,
  operator: NodeOperatorType,
) => {
  const nodeWithId = Object.entries(layout).find(([, node]) => node.props.dataKey === dataKey);
  if (!nodeWithId) return false;

  const props = nodeWithId[1].props;
  if (!isFieldWithOptions(props)) return true;
  if (!operatorsWithNotNullValue.includes(operator)) return true;

  const options = props.options.map((x) => x.key);
  if (Array.isArray(value)) return value.every((v) => options.includes(v));

  return options.includes(value);
};

export const isScoreSectionsContainsDataKey = (dataKey: string, scoreSections?: IBaseScoreSection[]) =>
  scoreSections?.some((x) => isExpressionContainsDataKey(x.expression, dataKey));

export const isVariableContainsDataKey = (dataKey: string, variableSections?: IBaseVariableSection[]) =>
  variableSections?.some((x) => isExpressionContainsDataKey(x.expression, dataKey));

export const isExpressionContainsDataKey = (expression: string | undefined, dataKey: string | undefined) =>
  expression?.toLowerCase().replaceAll('"', "'").includes(`\'${dataKey?.toLowerCase()}\'`);

// "Mark's step" value shouldn't be smaller then "Step" value
// Task 55193: Prevent input mark's step lower then step
export const checkSliderStepMarks = (layout: SerializedNodes | undefined): ValidationResultType => {
  return Object.entries(layout ?? {}).reduce((result, [nodeId, node]) => {
    if (!isSliderField(node.props)) return { ...result };

    if (!node.props.hideMarks && node.props.marksStep !== undefined && node.props.marksStep % node.props.step! !== 0)
      return {
        ...result,
        [nodeId]: {
          type: ValidationEnum.SliderMarks,
          message: i18next.t('GeneralFormBuilder.Errors.MarksStep'),
        },
      };

    return { ...result };
  }, {});
};

export const checkFileTypes = (layout: SerializedNodes | undefined): ValidationResultType => {
  return Object.entries(layout ?? {}).reduce((result, [nodeId, node]) => {
    if (!isAttachmentsField(node.props)) return { ...result };

    if (!node.props.fileTypes?.length)
      return {
        ...result,
        [nodeId]: {
          type: ValidationEnum.NoFileTypes,
          message: i18next.t('GeneralFormBuilder.Errors.NoFileTypes'),
        },
      };

    return { ...result };
  }, {});
};

export const expressionValidationRules =
  (scheme: SerializedNodes, variables: IBaseVariableSection[]) => (value: string) => {
    try {
      const answers = getMockedAnswers(scheme);
      executeSimpleExpression(value, answers, scheme, variables);
    } catch (error: any) {
      return error.message;
    }

    return undefined;
  };

const getMockedAnswers = (scheme: SerializedNodes) =>
  Object.entries(scheme).reduce(
    (result, [, node]) =>
      isFormFieldProps(node.props)
        ? {
            ...result,
            [node.props.dataKey]: mockedAnswerResolver(node),
          }
        : { ...result },
    {},
  );

const mockedAnswerResolver = (node: SerializedNode) => {
  // @ts-ignore
  const resolvedName: string = node.type.resolvedName;
  if (isFieldWithOptions(node.props)) {
    return {
      // Multiple value field
      FormMultipleDropdownInput: node.props.options.map((x) => x.key),
      FormCheckboxGroupInput: node.props.options.map((x) => x.key),

      // Single value field
      FormRadioGroupInput: _.first(node.props.options)?.key,
      FormDropdownInput: _.first(node.props.options)?.key,
    }[resolvedName];
  }

  return {
    // Single value field
    FormNumberInput: 4.65785,
    FormTimeInput: 1800000,
    FormSliderInput: 5,
    FormTextInput: 'Singleline mocked text',
    FormMultipleTextInput: 'Multiple line mocked text',
    FormTagInput: ['One', 'Two'],
    FormDateInput: new Date(),
    FormDateRangeInput: [new Date(), new Date()],
  }[resolvedName];
};

export const checkScoreCalculation = (
  layout: SerializedNodes | undefined,
  scoreSections?: IBaseScoreSection[],
  variables?: IBaseVariableSection[],
): ValidationResultType | undefined =>
  scoreSections?.reduce((result, variable) => {
    const message = expressionValidationRules(layout ?? {}, variables ?? [])(variable.expression);

    if (!isNullOrEmpty(message))
      return {
        ...result,
        [variable.name.trim()]: {
          type: ValidationEnum.ScoreSection,
          message: message,
        },
      };

    return { ...result };
  }, {});

export const dataKeyIsExistsInConditional = (
  layout: SerializedNodes | undefined,
  dataKey: string,
): { nodeId: string; title: string }[] => {
  if (!layout) return [];

  return Object.entries(layout).reduce((arr, [nodeId, node]) => {
    if (
      isConditionalProps(node.props) &&
      ((node.props.enableIf && ExpressionBuilder.fromString(node.props.enableIf).hasDataKey(dataKey)) ||
        (node.props.visibleIf && ExpressionBuilder.fromString(node.props.visibleIf).hasDataKey(dataKey)))
    )
      return [
        ...arr,
        {
          nodeId,
          title: (node.props as FormTitleProps).title ?? node.displayName,
        },
      ];

    return arr;
  }, [] as { nodeId: string; title: string }[]);
};
